// Orchestrates the registered third-party snippets. The core app wires a few
// stable entry points to this manager (see `index.js` for the thin wrappers):
//
//   - start()                on client entry / hydration
//   - handleConsentChange()  from OsanoProvider when consent changes
//   - handleFirstInteraction() when the pre-hydration detector fires
//   - handleRouteChange()    on every Gatsby route update
//
// Adding a new integration therefore never touches these core files: register a
// new `ThirdPartySnippet` subclass in `index.js` and it is driven automatically.
import { isBrowser, debug } from '../misc';
import { TRIGGERS } from './snippet';

// Window-backed registry of snippet ids that have already loaded. Kept on
// `window` (not just instance state) so that fast refresh / re-imports during
// development cannot cause a second injection within the same page load.
const LOADED_FLAG = '__splashtopThirdPartyLoaded';
// Event dispatched by the pre-hydration first-interaction detector in
// `gatsby-ssr.js`. Mirrored by a boolean flag for interactions that happen
// before this manager starts listening.
export const FIRST_INTERACTION_EVENT = 'splashtop:first-interaction';
export const FIRST_INTERACTION_FLAG = '__splashtopFirstInteraction';

// Builds the context handed to each snippet's `shouldLoad` / `onRouteChange`.
// Route-change locations (reach-router) carry no hostname, so fall back to the
// live window location; same-origin navigation keeps the host stable.
const buildContext = (trigger, location) => {
  const win = isBrowser ? window.location : {};
  const loc = location || win;
  return {
    trigger,
    hostname: loc.hostname || win.hostname || '',
    pathname: loc.pathname || '',
    search: loc.search || '',
    hash: loc.hash || ''
  };
};

export class SnippetManager {
  constructor(snippets = []) {
    this.snippets = [...snippets];
    this.consent = null;
    this.started = false;
    this.firstInteractionHandled = false;
    this.firstInteractionBound = false;
    // Instance-level record of loaded ids, mirrored to a window-backed guard so
    // a re-created manager (fast refresh / re-import) cannot double-inject.
    this.loaded = new Set();
    this.handleFirstInteractionEvent = this.handleFirstInteractionEvent.bind(this);
  }

  register(snippet) {
    this.snippets.push(snippet);
    return this;
  }

  isLoaded(id) {
    if (this.loaded.has(id)) return true;
    if (isBrowser && window[LOADED_FLAG] && window[LOADED_FLAG][id]) {
      this.loaded.add(id);
      return true;
    }
    return false;
  }

  markLoaded(id) {
    this.loaded.add(id);
    if (!isBrowser) return;
    if (!window[LOADED_FLAG]) window[LOADED_FLAG] = {};
    window[LOADED_FLAG][id] = true;
  }

  consentSatisfied(snippet) {
    if (!snippet.consentCategory) return true;
    return !!this.consent && this.consent[snippet.consentCategory] === 'ACCEPT';
  }

  tryLoad(snippet, ctx) {
    if (this.isLoaded(snippet.id)) return false;
    if (!this.consentSatisfied(snippet)) return false;

    let shouldLoad = false;
    try {
      shouldLoad = snippet.shouldLoad(ctx);
    } catch (error) {
      debug('third-party: shouldLoad threw', snippet.id, error);
      return false;
    }
    if (!shouldLoad) return false;

    try {
      snippet.load(ctx);
      this.markLoaded(snippet.id);
      debug('third-party: loaded', snippet.id, `(${ctx.trigger})`);
      return true;
    } catch (error) {
      debug('third-party: load failed', snippet.id, error);
      return false;
    }
  }

  evaluateTrigger(trigger, location) {
    if (!isBrowser) return;
    const ctx = buildContext(trigger, location);
    this.snippets.forEach((snippet) => {
      if (snippet.hasTrigger(trigger)) this.tryLoad(snippet, ctx);
    });
  }

  start(location) {
    if (!isBrowser || this.started) return;
    this.started = true;
    this.bindFirstInteraction();
    this.evaluateTrigger(TRIGGERS.IMMEDIATE, location);
  }

  // Called by OsanoProvider with the full consent object. Stores it and
  // (re)evaluates consent-gated snippets; each still loads at most once.
  handleConsentChange(consent) {
    this.consent = consent || null;
    this.evaluateTrigger(TRIGGERS.CONSENT, null);
  }

  // Idempotent: first interaction is a one-shot signal per full page load.
  handleFirstInteraction(location) {
    if (!isBrowser || this.firstInteractionHandled) return;
    this.firstInteractionHandled = true;
    this.evaluateTrigger(TRIGGERS.FIRST_INTERACTION, location);
  }

  handleRouteChange(location) {
    if (!isBrowser) return;
    const ctx = buildContext(TRIGGERS.ROUTE_CHANGE, location);
    this.snippets.forEach((snippet) => {
      if (snippet.hasTrigger(TRIGGERS.ROUTE_CHANGE)) this.tryLoad(snippet, ctx);
    });
    // Per-navigation side effects for snippets that are already live.
    this.snippets.forEach((snippet) => {
      if (!this.isLoaded(snippet.id)) return;
      try {
        snippet.onRouteChange(ctx);
      } catch (error) {
        debug('third-party: onRouteChange threw', snippet.id, error);
      }
    });
  }

  handleFirstInteractionEvent() {
    this.handleFirstInteraction(isBrowser ? window.location : null);
  }

  bindFirstInteraction() {
    if (!isBrowser || this.firstInteractionBound) return;
    this.firstInteractionBound = true;
    window.addEventListener(
      FIRST_INTERACTION_EVENT,
      this.handleFirstInteractionEvent,
      { once: true }
    );
    // The detector may have fired before this manager initialized.
    if (window[FIRST_INTERACTION_FLAG]) this.handleFirstInteractionEvent();
  }
}

export default SnippetManager;
